import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBlogCategories } from '@/hooks/useBlogCategories';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const allLanguages = [
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

interface LanguageSwitcherProps {
  onLanguageChange?: () => void;
}

export const LanguageSwitcher = ({ onLanguageChange }: LanguageSwitcherProps = {}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { categories } = useBlogCategories();
  const { isEnabled: maintenanceEnabled } = useMaintenanceMode();

  // Load enabled languages from cache or default to all
  const cachedEnabledLanguages = sessionStorage.getItem('nav-enabled-languages');
  const [enabledLanguages, setEnabledLanguages] = useState(() => {
    if (cachedEnabledLanguages) {
      try {
        return JSON.parse(cachedEnabledLanguages);
      } catch (e) {
        return { pt: true, en: true, es: true };
      }
    }
    return { pt: true, en: true, es: true };
  });

  useEffect(() => {
    const fetchEnabledLanguages = async () => {
      const { data } = await supabase
        .from('site_content')
        .select('content_value')
        .eq('content_key', 'nav_enabled_languages')
        .single();

      if (data?.content_value) {
        try {
          const parsed = JSON.parse(data.content_value);
          // Ensure Portuguese is always enabled (it's the default language)
          const languagesWithPt = { ...parsed, pt: true };
          setEnabledLanguages(languagesWithPt);
          sessionStorage.setItem('nav-enabled-languages', JSON.stringify(languagesWithPt));
        } catch (e) {
          // If parsing fails, use default
        }
      }
    };

    fetchEnabledLanguages();
  }, []);

  // Ensure Portuguese is always enabled
  const safeEnabledLanguages = { ...enabledLanguages, pt: true };

  // Filter languages based on what's enabled
  const languages = allLanguages.filter(lang => safeEnabledLanguages[lang.code as keyof typeof safeEnabledLanguages]);

  // If only Portuguese is enabled, don't show the switcher at all
  // (no point showing a language switcher with only one option)
  if (languages.length <= 1) {
    return null;
  }

  // Normalize current language to handle variants like 'en-US' -> 'en'
  const currentLang = i18n.language?.split('-')[0] || 'pt';
  const currentLanguage = languages.find(lang => lang.code === currentLang) || languages[0];

  const changeLanguage = async (languageCode: string) => {
    // Save current scroll position and hash
    const scrollY = window.scrollY;
    const currentHash = location.hash;

    i18n.changeLanguage(languageCode);

    // If maintenance mode is active, don't navigate - just change language in place
    if (maintenanceEnabled) {
      setIsOpen(false);
      if (onLanguageChange) {
        onLanguageChange();
      }
      return;
    }

    // URL slug mappings for localized pages
    const slugMappings = {
      // Privacy Policy
      'politica-de-privacidade': { pt: 'politica-de-privacidade', en: 'privacy-policy', es: 'politica-de-privacidad' },
      'privacy-policy': { pt: 'politica-de-privacidade', en: 'privacy-policy', es: 'politica-de-privacidad' },
      'politica-de-privacidad': { pt: 'politica-de-privacidade', en: 'privacy-policy', es: 'politica-de-privacidad' },
      // Terms of Service
      'termos-de-servico': { pt: 'termos-de-servico', en: 'terms-of-service', es: 'terminos-de-servicio' },
      'terms-of-service': { pt: 'termos-de-servico', en: 'terms-of-service', es: 'terminos-de-servicio' },
      'terminos-de-servicio': { pt: 'termos-de-servico', en: 'terms-of-service', es: 'terminos-de-servicio' },
    };

    // Update URL based on language
    const currentPath = location.pathname;
    let newPath = currentPath;

    // Remove existing language prefix
    if (currentPath.startsWith('/en/') || currentPath.startsWith('/es/')) {
      newPath = currentPath.substring(3); // Remove /en or /es
    } else if (currentPath.startsWith('/en') || currentPath.startsWith('/es')) {
      newPath = currentPath.substring(3) || '/';
    }

    // Check if we're on a blog post page
    const pathParts = newPath.split('/').filter(Boolean);
    const isBlogPost = pathParts[0] === 'blog' && pathParts.length === 2;

    if (isBlogPost) {
      // For blog posts, fetch the post to get the correct slug for the target language
      const currentSlug = pathParts[1];
      try {
        // Try to find the post by any slug variant
        let { data: post } = await supabase
          .from('blog_posts')
          .select('slug, slug_en, slug_es')
          .or(`slug.eq.${currentSlug},slug_en.eq.${currentSlug},slug_es.eq.${currentSlug}`)
          .maybeSingle();

        if (post) {
          // Get the appropriate slug for the target language
          let targetSlug = post.slug; // Default to Portuguese
          if (languageCode === 'en' && post.slug_en) {
            targetSlug = post.slug_en;
          } else if (languageCode === 'es' && post.slug_es) {
            targetSlug = post.slug_es;
          }
          pathParts[1] = targetSlug;
          newPath = '/' + pathParts.join('/');
        }
      } catch (error) {
        // Continue with original slug if error
      }
    } else if (pathParts.length > 0) {
      // Translate static page slugs
      const lastPart = pathParts[pathParts.length - 1];
      if (slugMappings[lastPart as keyof typeof slugMappings]) {
        const translatedSlug = slugMappings[lastPart as keyof typeof slugMappings][languageCode as 'pt' | 'en' | 'es'];
        pathParts[pathParts.length - 1] = translatedSlug;
        newPath = '/' + pathParts.join('/');
      }
    }

    // Add new language prefix (except for Portuguese, which is default)
    if (languageCode === 'en') {
      newPath = '/en' + (newPath === '/' ? '' : newPath);
    } else if (languageCode === 'es') {
      newPath = '/es' + (newPath === '/' ? '' : newPath);
    }

    // Ensure path always starts with /
    if (!newPath.startsWith('/')) {
      newPath = '/' + newPath;
    }

    // Translate category query parameter if present
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get('category');
    if (categoryParam && categories.length > 0) {
      // Find the category by matching any translation
      const categoryObj = categories.find(c =>
        c.name === categoryParam ||
        c.name_en === categoryParam ||
        c.name_es === categoryParam
      );

      if (categoryObj) {
        // Get the translated name for the target language
        let translatedName = categoryObj.name; // Default to Portuguese
        if (languageCode === 'en' && categoryObj.name_en) {
          translatedName = categoryObj.name_en;
        } else if (languageCode === 'es' && categoryObj.name_es) {
          translatedName = categoryObj.name_es;
        }
        searchParams.set('category', translatedName);
      }
    }

    // Build final URL with translated query params and preserve hash
    const queryString = searchParams.toString();
    const finalPath = queryString ? `${newPath}?${queryString}${currentHash}` : `${newPath}${currentHash}`;

    navigate(finalPath, { replace: true });

    // Restore scroll position after navigation and content loads
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (currentHash) {
          // If there's a hash, scroll to that element
          const element = document.querySelector(currentHash);
          if (element) {
            element.scrollIntoView({ behavior: 'instant', block: 'start' });
          } else {
            window.scrollTo(0, scrollY);
          }
        } else {
          window.scrollTo(0, scrollY);
        }
      });
    });

    // Close the dropdown
    setIsOpen(false);

    // Call the callback if provided (to close mobile menu)
    if (onLanguageChange) {
      onLanguageChange();
    }
  };

  return (
    <DropdownMenu modal={false} open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 w-full md:w-32"
        >
          <span className="hidden md:inline">{currentLanguage.flag} {currentLanguage.name}</span>
          <span className="md:hidden flex items-center justify-center w-full">{currentLanguage.flag} {currentLanguage.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={5} className="z-[100]">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onSelect={(e) => {
              e.preventDefault();
              changeLanguage(language.code);
            }}
            className={`cursor-pointer ${currentLang === language.code ? 'bg-accent/10' : ''}`}
          >
            <span className="mr-2">{language.flag}</span>
            {language.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
